'use client';

import React from 'react';
import Image from 'next/image';
import { Users } from 'lucide-react';
import { useEventSettings } from '@/context/EventSettingsContext';

export default function OrganisingTeamSection() {
  const { settings, isLoading } = useEventSettings();
  const teamMembers = settings?.teamMembers ?? [];

  // While loading, show a subtle skeleton so the section doesn't flash in/out
  if (isLoading) {
    return (
      <section className="w-full bg-gradient-to-b from-[#0d1f3c] to-[#0a1628] py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-lg bg-sky-600/20 border border-sky-500/30 animate-pulse" />
            <div className="h-3 w-24 bg-white/10 rounded animate-pulse" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-8">
            {[...Array(5)]?.map((_, i) => (
              <div key={i} className="flex flex-col items-center gap-3">
                <div className="w-20 h-20 rounded-full bg-white/10 animate-pulse" />
                <div className="h-3 w-16 bg-white/10 rounded animate-pulse" />
                <div className="h-2 w-12 bg-white/10 rounded animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Hide section if no team members after loading completes
  if (teamMembers?.length === 0) return null;

  return (
    <section className="w-full bg-gradient-to-b from-[#0d1f3c] to-[#0a1628] py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Section Header */}
        <div className="mb-8 sm:mb-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-sky-600/20 border border-sky-500/30 flex items-center justify-center flex-shrink-0">
              <Users size={16} className="text-sky-400" />
            </div>
            <span className="text-sky-400 text-xs font-bold uppercase tracking-widest">Our Team</span>
          </div>
          <h2 className="text-white font-extrabold text-2xl sm:text-3xl leading-tight mb-2">
            Organising Team
            <span className="block text-sky-300 text-lg sm:text-xl font-bold mt-1">National BLS Day 2026</span>
          </h2>
          <p className="text-white/50 text-sm max-w-lg">
            Meet the dedicated team behind the event — the organisers who made this life-saving initiative possible.
          </p>
        </div>

        {/* Team Members Grid — responsive: 2 cols on mobile, 3 on sm, 4 on md, 5 on lg */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-8">
          {teamMembers?.map((member, index) => (
            <div
              key={index}
              className="flex flex-col items-center text-center group"
            >
              {/* Photo */}
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-2 border-sky-500/40 bg-white/10 mb-3 group-hover:border-sky-400/70 transition-all duration-300 shadow-lg flex-shrink-0">
                {member?.photo ? (
                  <Image
                    src={member?.photo}
                    alt={`${member?.name || 'Team member'} — ${member?.role || 'Organiser'}`}
                    fill
                    className="object-cover"
                    sizes="(max-width: 640px) 64px, (max-width: 768px) 80px, 96px"
                    unoptimized
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-sky-800/40 to-blue-900/40">
                    <Users size={24} className="text-sky-400/60" />
                  </div>
                )}
              </div>

              {/* Name */}
              <p className="text-white font-semibold text-xs sm:text-sm leading-tight mb-1 px-1 break-words w-full">
                {member?.name}
              </p>

              {/* Role */}
              <p className="text-sky-300/80 text-xs font-medium leading-tight px-1 break-words w-full">
                {member?.role}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
